import {
  Button,
  DatePickerFieldRhf,
  ImageField,
  InputFieldRhf,
  SizableText,
  XStack,
  YStack,
  TimePickerFieldRhf,
  Form,
} from '@my/ui'
import { UserAvatar } from 'app/features/user/components'
import { ChevronDown } from '@tamagui/lucide-icons'
import { Group, User } from 'app/types'
import { PostCreationHeader } from '../post-creation-header'
import { useState } from 'react'
import { useImagePicker } from 'app/hooks'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { setHours, setMinutes } from 'date-fns'
import { KeyboardAwareScrollView } from 'app/components'

type EventCreationViewProps = {
  title: string
  user: Pick<User, 'fullName' | 'avatar'>
  selectedGroup?: Pick<Group, 'id' | 'name'> | null
  defaultValues?: EventFormData
  defaultImage?: string | null
  onOpenChange: (open: boolean) => void
  onGroupSelectPress?: () => void
  onBack?: () => void
  onSubmit: (data: SubmitEventData, cb: () => void) => void
  isSubmitting?: boolean
  isEditing?: boolean
}

const timeRegex = /^([0-1][0-9]|2[0-3]):?([0-5][0-9])$/
const formSchema = z
  .object({
    name: z
      .string()
      .min(3, 'Event name should be longer than 3 characters')
      .max(50, "Event name can't be longer than 50 characters"),
    description: z
      .string()
      .max(500, "Event description can't be longer than 500 characters")
      .optional(),
    startDate: z.string().min(2, 'Start date is required'),
    startTime: z.string().min(2, 'Start time is required'),
    endDate: z.string().optional(),
    endTime: z.string().optional(),
    location: z.string().max(50, "Location can't be longer than 50 characters").optional(),
  })
  .superRefine((data, ctx) => {
    if (data.startTime && !timeRegex.test(data.startTime)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Start time is invalid',
        path: ['startTime'],
      })
    }

    if (data.endTime && !timeRegex.test(data.endTime)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'End time is invalid',
        path: ['endTime'],
      })
    }

    if (data.endDate) {
      const startDate = new Date(data.startDate)
      const endDate = new Date(data.endDate)

      // Check that end date is after start date
      if (endDate < startDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'End date must be after start date',
          path: ['endDate'],
        })
      }

      // If start/end dates are equal, make sure end time is after start time
      if (data.endDate === data.startDate) {
        if (data.endTime) {
          const currentDate = new Date()

          const [endHours, endMinutes] = data.endTime.split(':').map(Number) as [number, number]
          const [startHours, startMinutes] = data.startTime.split(':').map(Number) as [
            number,
            number
          ]

          const endDateTime = setMinutes(setHours(currentDate, endHours), endMinutes)
          const startDateTime = setMinutes(setHours(currentDate, startHours), startMinutes)

          if (endDateTime <= startDateTime) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'End time must be after start time',
              path: ['endTime'],
            })
          }
        } else {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'End time is required',
            path: ['endTime'],
          })
        }
      }
    }
  })

export type EventFormData = z.infer<typeof formSchema>

export interface SubmitEventData extends EventFormData {
  image: string | null
  group: Pick<Group, 'id' | 'name'>
}

export const EventCreationView = ({
  title,
  user,
  onOpenChange,
  selectedGroup,
  defaultValues,
  defaultImage,
  onBack,
  onGroupSelectPress,
  onSubmit,
  isSubmitting,
  isEditing,
}: EventCreationViewProps) => {
  const [eventImage, setEventImage] = useState<string | null>(defaultImage || null)

  const { control, handleSubmit, reset } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...defaultValues,
    },
  })

  const { pickImage, loading: pickImageLoading } = useImagePicker({
    onChange: (image) => setEventImage(image.uri),
    constraints: {
      minWidth: 100,
      minHeight: 100,
    },
    resize: {
      width: 1000,
    },
  })

  const handleSubmitEvent = async (data: EventFormData) => {
    if (!selectedGroup || !canPost) {
      return
    }
    onSubmit(
      {
        group: selectedGroup,
        image: eventImage,
        ...data,
      },
      () => {
        reset()
      }
    )
  }

  // Allow posting if a group is selected
  const canPost = !!selectedGroup?.id
  return (
    <YStack flex={1}>
      <Form gap="$3.5" flex={1} onSubmit={handleSubmit(handleSubmitEvent)}>
        {/* Header */}
        <PostCreationHeader
          title={title}
          onBack={onBack}
          {...(!onBack && { onClose: () => onOpenChange(false) })}
        >
          <Form.Trigger asChild ai="center" fd="row">
            <Button
              size="$3"
              onPress={() => {}}
              theme="primary"
              disabled={!canPost}
              opacity={canPost ? 1 : 0.5}
              loading={isSubmitting}
              $gtSm={{
                size: '$4',
              }}
            >
              Publish
            </Button>
          </Form.Trigger>
        </PostCreationHeader>

        <KeyboardAwareScrollView style={{ flex: 1 }}>
          <YStack flex={1} gap="$3.5" pb="$4">
            <XStack w="100%" gap="$3.5" ai="flex-start">
              <UserAvatar src={user?.avatar?.small} width={44} height={44} />
              <YStack flex={1} gap="$1">
                <SizableText size="$3" fow="500">
                  {user?.fullName}
                </SizableText>
                <XStack gap="$2" flexWrap="wrap">
                  {(onGroupSelectPress || isEditing) && (
                    <Button
                      theme="gray"
                      size="$1"
                      px="$1.5"
                      {...(!isEditing && {
                        onPress: onGroupSelectPress,
                        iconAfter: <ChevronDown size={12} />,
                      })}
                    >
                      <SizableText
                        size="$2"
                        fow="500"
                        numberOfLines={1}
                        maxWidth={isEditing ? 'auto' : 100}
                      >
                        {selectedGroup ? selectedGroup.name : 'Select Group'}
                      </SizableText>
                    </Button>
                  )}
                  {!selectedGroup && !isEditing && (
                    <SizableText size="$1">Select a group to publish</SizableText>
                  )}
                </XStack>
              </YStack>
            </XStack>

            <ImageField
              width="100%"
              onPress={pickImage}
              image={eventImage}
              loading={pickImageLoading}
            />

            <InputFieldRhf
              name="name"
              control={control}
              label="Event Name"
              placeholder="Event Name"
              required
              fullWidth
              shadowOpacity={0}
              showHelperText
              boc="$gray4"
              hoverStyle={{
                boc: '$gray6',
              }}
              focusStyle={{
                boc: '$gray7',
                outlineWidth: 0,
              }}
            />

            <InputFieldRhf
              name="description"
              control={control}
              label="Event description"
              placeholder="Event description"
              fullWidth
              shadowOpacity={0}
              multiline
              rows={4}
              showHelperText
              boc="$gray4"
              minHeight={100}
              hoverStyle={{
                boc: '$gray6',
              }}
              focusStyle={{
                boc: '$gray7',
                outlineWidth: 0,
              }}
            />

            <XStack gap="$2" $gtSm={{ gap: '$3.5' }}>
              <DatePickerFieldRhf
                name="startDate"
                control={control}
                label="Start Date"
                placeholder="Start Date"
                required
                fullWidth
                containerProps={{
                  flex: 1,
                  flexBasis: '49%',
                }}
                popoverProps={{
                  placement: 'top',
                }}
                inputProps={{
                  backgroundColor: '$white',
                }}
              />

              <TimePickerFieldRhf
                name="startTime"
                control={control}
                label="Start Time"
                placeholder="Start Time"
                required
                containerProps={{
                  flex: 1,
                  flexBasis: '49%',
                }}
                popoverProps={{
                  placement: 'top',
                }}
                inputProps={{
                  backgroundColor: '$white',
                }}
              />
            </XStack>

            <XStack gap="$2" $gtSm={{ gap: '$3.5' }}>
              <DatePickerFieldRhf
                name="endDate"
                control={control}
                label="End Date"
                placeholder="End Date"
                fullWidth
                containerProps={{
                  flex: 1,
                  flexBasis: '49%',
                }}
                popoverProps={{
                  placement: 'top',
                }}
                inputProps={{
                  backgroundColor: '$white',
                }}
              />

              <TimePickerFieldRhf
                name="endTime"
                control={control}
                label="End Time"
                placeholder="End Time"
                containerProps={{
                  flex: 1,
                  flexBasis: '49%',
                }}
                popoverProps={{
                  placement: 'top',
                }}
                inputProps={{
                  backgroundColor: '$white',
                }}
              />
            </XStack>

            <InputFieldRhf
              name="location"
              control={control}
              label="Event Location"
              placeholder="Event Location"
              fullWidth
              shadowOpacity={0}
              showHelperText
              boc="$gray4"
              hoverStyle={{
                boc: '$gray6',
              }}
              focusStyle={{
                boc: '$gray7',
                outlineWidth: 0,
              }}
            />
          </YStack>
        </KeyboardAwareScrollView>
      </Form>
    </YStack>
  )
}
